/**
 * src/components/AdminRoute.tsx — Route guard for admin-only pages.
 *
 * Only users with role === 'admin' may access admin routes.
 * All other roles (customer, driver, both, null) redirect to /dashboard.
 */

import { useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useCustomerRole } from '../hooks/useCustomerRole';
import { LoadingFallback } from './LoadingFallback';

export function AdminRoute() {
  const { role, loading } = useCustomerRole();

  // Only block on the INITIAL role resolution. useCustomerRole
  // (=useCustomerProfile) re-enters loading on every background refetch
  // (roleVersion bumps, token settle); returning LoadingFallback on those would
  // unmount + remount the whole admin subtree each flap. The profile keeps its
  // value during a refetch, so `role` stays valid — keep the subtree mounted.
  // (Mirrors CustomerRoleRoute; that flap stalled the gated onboarding flow.)
  const resolvedOnceRef = useRef(false);
  if (!loading) resolvedOnceRef.current = true;
  if (loading && !resolvedOnceRef.current) return <LoadingFallback />;
  if (role === 'admin') return <Outlet />;
  return <Navigate to="/dashboard" replace />;
}
